// Updated EventSetlistModal.js with song order preservation and mobile improvements
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import SetlistTab from '../event_modal/SetlistTab';
import { fetchSongs } from '../../../../services/songService';
import { updateEvent } from '../../../../services/eventService';
import { createSetlist, updateSetlist } from '../../../../services/setlistService';
import { useAuth } from '../../../../contexts/AuthContext';
import { useLanguage } from '../../../../contexts/LanguageContext';
import '../../../MobileModals.css';

function EventSetlistModal({ event, setlist, onClose, onSave }) {
    const { user } = useAuth();
    const { t } = useLanguage();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [modalRoot, setModalRoot] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    // Setlist management
    const [songs, setSongs] = useState([]);
    const [selectedSongs, setSelectedSongs] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [checkedSongs, setCheckedSongs] = useState({});

    // Check for mobile device on resize and mount
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        window.addEventListener('resize', handleResize);
        handleResize(); // Initial check
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Find or create the modal root element
    useEffect(() => {
        let root = document.getElementById('modal-root');
        if (!root) {
            root = document.createElement('div');
            root.id = 'modal-root';
            document.body.appendChild(root);
        }
        setModalRoot(root);

        // Clean up function
        return () => {
            if (root && root.childNodes.length === 0) {
                document.body.removeChild(root);
            }
        };
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                // Get community ID from the event
                const communityId = event.community_id;
                
                // Fetch all songs
                const songData = await fetchSongs();
                setSongs(songData || []);

                // Set initial selected songs if we have a setlist
                if (setlist && setlist.songs) {
                    
                    // Ensure the songs have order property
                    const songsWithOrder = setlist.songs.map((song, index) => ({
                        ...song,
                        order: song.order !== undefined ? song.order : index
                    }));
                    
                    // Sort by order if available
                    const sortedSongs = songsWithOrder.sort((a, b) => a.order - b.order);
                    setSelectedSongs(sortedSongs);
                    
                    // Set checked songs based on setlist
                    const initialCheckedSongs = {};
                    sortedSongs.forEach(song => {
                        initialCheckedSongs[song.song_id] = true;
                    });
                    setCheckedSongs(initialCheckedSongs);
                }
            } catch (error) {
                setError(t('events.messages.errorLoadingSongs'));
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [setlist, event.community_id, user?.id]);

    const handleSave = async () => {
        setSaving(true);
        setError('');

        try {
            const setlistName = `${event.title} - ${event.date}`;
            
            // Ensure each song has an order field that matches its position in the array
            const songsWithOrder = selectedSongs.map((song, index) => ({
                ...song,
                order: index  // Set order based on current position in the array
            }));
            
            const setlistData = {
                name: setlistName,
                event_id: event.id,
                songs: songsWithOrder,
                // Add community context if available
                community_id: event.community_id,
                owner_type: event.community_id ? 'community' : 'user',
                owner_id: event.community_id || user?.id
            };

            if (event.setlist_id) {
                // Update existing setlist
                await updateSetlist(event.setlist_id, setlistData);
            } else {
                // Create new setlist and link to event
                const setlistResponse = await createSetlist(setlistData);
                
                if (setlistResponse?.setlist?.id) {
                    // Link the setlist to the event
                    await updateEvent(event.id, {
                        ...event,
                        setlist_id: setlistResponse.setlist.id,
                        time: event.time && typeof event.time === 'string' && event.time.includes(':') 
                        ? event.time.slice(0, 5)  // Get only HH:MM part
                        : event.time
                    });
                }
            }

            if (typeof onSave === 'function') {
                onSave();
            }
        } catch (error) {
            setError(t('events.messages.errorSavingSetlist'));
        } finally {
            setSaving(false);
        }
    };

    // Trap focus within modal for accessibility
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        
        // Add body class to prevent scrolling
        document.body.classList.add('modal-open');
        
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.classList.remove('modal-open');
        };
    }, [onClose]);

    // If modal root is not available yet, don't render anything
    if (!modalRoot) return null;

    // Calculate modal width based on screen size
    const getModalWidth = () => {
        if (isMobile) return '100%';
        
        // For desktop, use 2/3 of the viewport width, but capped at 1200px
        const viewportWidth = window.innerWidth;
        const twoThirdsWidth = Math.floor(viewportWidth * 0.66);
        return Math.min(twoThirdsWidth, 1200) + 'px';
    };

    // Use createPortal to render the modal outside the component hierarchy
    return createPortal(
        <div className="modal-overlay">
            <div className="modal-container" style={{ maxWidth: getModalWidth() }}>
                <div className="card border-0 h-100 d-flex flex-column">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
                        <h5 className="card-title mb-0" style={{ fontSize: isMobile ? '0.9rem' : '1.15rem' }}>
                            {t('events.modals.setlist.title')}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>

                    <div className={`card-body ${isMobile ? 'p-1 pt-2' : 'p-2 p-md-3'} overflow-auto`} 
                         style={{ maxHeight: isMobile ? 'calc(85vh - 100px)' : '600px' }}>
                        {error && 
                            <div className="alert alert-danger py-2 px-3" style={{ fontSize: isMobile ? '0.75rem' : '0.9rem' }}>
                                {error}
                            </div>
                        }
                        
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">{t('navigation.loading')}</span>
                                </div>
                            </div>
                        ) : (
                            <SetlistTab
                                songs={songs}
                                selectedSongs={selectedSongs}
                                setSelectedSongs={setSelectedSongs}
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                                checkedSongs={checkedSongs}
                                setCheckedSongs={setCheckedSongs}
                                communityId={event.community_id}
                                isMobile={isMobile}
                                eventData={event}
                            />
                        )}
                    </div>

                    <div className={`card-footer bg-white d-flex justify-content-end ${isMobile ? 'py-1' : 'py-2'}`}>
                        <button
                            type="button"
                            className={`btn btn-secondary me-2 ${isMobile ? 'btn-sm' : ''}`}
                            onClick={onClose}
                            disabled={saving}
                            style={{ fontSize: isMobile ? '0.75rem' : '0.9rem' }}
                        >
                            {t('events.modals.setlist.cancel')}
                        </button>
                        <button
                            type="button"
                            className={`btn btn-primary ${isMobile ? 'btn-sm' : ''}`}
                            onClick={handleSave}
                            disabled={saving}
                            style={{ fontSize: isMobile ? '0.75rem' : '0.9rem' }}
                        >
                            {saving ? t('events.modals.setlist.saving') : t('events.modals.setlist.save')}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        modalRoot
    );
}

export default EventSetlistModal;